'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Plus, Square, Loader2, MoreVertical, Copy, Trash2, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ChatMeta } from '@/lib/chat-storage'
import type { RunMeta } from '@/lib/run-storage'
import { AVAILABLE_AGENTS } from '@/lib/chat-config'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

// Extended chat type with run info
type ChatWithRun = ChatMeta & {
  run?: RunMeta;
  status?: 'idle' | 'running' | 'stopped' | 'error' | 'idle_timeout';
  usage?: { inputTokens: number; outputTokens: number; totalCost: number };
  matchType?: 'title' | 'content';
}

interface ChatListProps {
  chats: ChatWithRun[]
  selectedChatId: string | null
  onNewChat: () => void
  onSelectChat: (chatId: string) => void
  onDeleteChat: (chatId: string) => void
  onStopChat: (chatId: string) => void
  onLoadMore?: () => void
  onSearch?: (query: string) => void
  isCreatingChat?: boolean
  deletingChatId?: string | null
  stoppingChatId?: string | null
  isLoadingMore?: boolean
  hasMore?: boolean
  searchQuery?: string
  className?: string
}

export function ChatList({
  chats,
  selectedChatId,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onStopChat,
  onLoadMore,
  onSearch,
  isCreatingChat = false,
  deletingChatId = null,
  stoppingChatId = null,
  isLoadingMore = false,
  hasMore = false,
  searchQuery = '',
  className,
}: ChatListProps) {
  // Track if component is mounted to avoid hydration mismatch with dates
  const [isClient, setIsClient] = useState(false)
  const [chatToDelete, setChatToDelete] = useState<string | null>(null)
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Sync local search with parent
  useEffect(() => {
    setLocalSearchQuery(searchQuery)
  }, [searchQuery])

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchQuery(value)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      onSearch?.(value)
    }, 300)
  }, [onSearch])

  // Clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearchQuery('')
    onSearch?.('')
  }, [onSearch])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || isLoadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore?.()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(loadMoreRef.current)

    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, onLoadMore])

  const formatDate = (dateString: string) => {
    // During SSR, return empty to avoid hydration mismatch
    if (!isClient) return ''

    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()

    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return date.toLocaleDateString()
  }

  const handleCopyId = async (chatId: string) => {
    try {
      await navigator.clipboard.writeText(chatId)
    } catch (error) {
      console.error('[ChatList] Failed to copy ID:', error)
    }
  }

  const handleConfirmDelete = () => {
    if (chatToDelete) {
      onDeleteChat(chatToDelete)
      setChatToDelete(null)
    }
  }

  return (
    <>
      <div className={cn("w-64 flex flex-col h-full overflow-hidden bg-card border-r", className)}>
        <div className="p-3 border-b space-y-2">
          <Button onClick={onNewChat} className="w-full gap-2" disabled={isCreatingChat}>
            {isCreatingChat ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                New Chat
              </>
            )}
          </Button>

          {/* Search input */}
          {onSearch && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search chats..."
                value={localSearchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8 pr-8 h-9 text-sm"
              />
              {localSearchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                  onClick={handleClearSearch}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 relative">
          <ScrollArea className="h-full" viewportRef={scrollAreaRef}>
            <div className="p-2 space-y-1">
              {chats.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {localSearchQuery ? 'No matching chats' : 'No chats yet'}
                </p>
              ) : (
                chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={cn(
                      'group relative flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-muted transition-colors',
                      selectedChatId === chat.id && 'bg-muted'
                    )}
                    onClick={() => onSelectChat(chat.id)}
                  >
                    <div className="flex-1 min-w-0 pr-8 lg:pr-0 lg:group-hover:pr-8 transition-[padding] duration-200">
                      <p className="text-sm font-medium truncate">{chat.title}</p>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="truncate">{formatDate(chat.updated_at)}</span>
                        {chat.status === 'running' && (
                          <span className="text-green-500 shrink-0">●</span>
                        )}
                        {chat.config?.agent && (
                          <span className="shrink-0 text-[9px] uppercase font-bold px-1 py-0.5 rounded bg-muted border">
                            {AVAILABLE_AGENTS.find(a => a.id === chat.config?.agent)?.name || chat.config.agent}
                          </span>
                        )}
                        {chat.matchType === 'content' && (
                          <span className="shrink-0 text-[10px] italic opacity-70">
                            in messages
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={cn(
                      "absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-0.5",
                      "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200"
                    )}>
                      {chat.status === 'running' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={stoppingChatId === chat.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            onStopChat(chat.id)
                          }}
                          title="Stop"
                        >
                          {stoppingChatId === chat.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Square className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => handleCopyId(chat.id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy ID
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => setChatToDelete(chat.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))
              )}

              {/* Load more trigger */}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="flex justify-center py-4"
                >
                  {isLoadingMore && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Bottom gradient fade */}
          <div className="absolute bottom-0 left-0 right-2.5 h-6 bg-gradient-to-t from-card to-transparent pointer-events-none z-10" />
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!chatToDelete} onOpenChange={(open) => !open && setChatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The chat and all its messages will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleConfirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
